/**
 * Generated With: pnpm x packages/api/tools/generate-shopify-rest-model.ts https://shopify.dev/api/admin/rest/reference/orders/transaction
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import { graphqlMoneyBagField, graphqlMoneyBagFieldToRest, shopifyCurrencyCodeEnumOptions } from "./constants";
import {
  ShopifyBalanceTransactionKey,
  ShopifyBalanceTransactionToOrderTransactionKey,
  ShopifyLocationKey,
  ShopifyOrderKey,
  ShopifyOrderTransactionKey,
  ShopifyOrderTransactionToBalanceTransactionsKey,
  ShopifyOrderTransactionToChildrenKey,
  ShopifyOrderTransactionToLocationKey,
  ShopifyOrderTransactionToOrderKey,
  ShopifyOrderTransactionToParentKey,
  ShopifyOrderTransactionToRefundKey,
  ShopifyOrderTransactionToShopKey,
  ShopifyRefundKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// we added shopify field keys in 2025-04 which changes how the static keys are generated. these are here so that they remain the same
const manualPaymentGatewayKey = "ModelField-DataModel-Shopify-OrderTransaction-manualPaymentGateway";
const manualPaymentGatewayStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-OrderTransaction-manualPaymentGateway-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyOrderTransactionKey,
  "2022-01": {
    name: "Shopify Order Transaction",
    fields: [
      {
        name: "Amount",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "amount" } },
      },
      {
        name: "Authorization",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "authorization" } },
      },
      {
        name: "Authorization Expires At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "authorization_expires_at" } },
      },
      {
        name: "Balance Transactions",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderTransactionToBalanceTransactionsKey,
        configuration: {
          relatedModelKey: ShopifyBalanceTransactionKey,
          inverseFieldKey: ShopifyBalanceTransactionToOrderTransactionKey,
        },
      },
      {
        name: "Children",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderTransactionToChildrenKey,
        configuration: {
          relatedModelKey: ShopifyOrderTransactionKey,
          inverseFieldKey: ShopifyOrderTransactionToParentKey,
        },
      },
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "currency" } },
      },
      // TODO: needs X-Shopify-Api-Features = include-currency-exchange-adjustments header
      // {
      //   name: "Currency Exchange Adjustment",
      //   fieldType: FieldType.BelongsTo,
      //   key: ShopifyOrderTransactionToCurrencyExchangeAdjustmentKey,
      //   contributorConfiguration:  { [ShopifyDominion.id]: { shopifyFieldKey: "currency_exchange_adjustment" } },
      //   configuration: {
      //     relatedModelKey: ShopifyCurrencyExchangeAdjustmentKey,
      //   },
      // },
      // {
      //   name: "Device",
      //   fieldType: FieldType.BelongsTo,
      //   key: ShopifyOrderTransactionToDeviceKey,
      //   contributorConfiguration:  { [ShopifyDominion.id]: { shopifyFieldKey: "device_id" } },
      //   configuration: {
      //     relatedModelKey: ShopifyDeviceKey,
      //   },
      // },
      {
        name: "Error Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "error_code" } },
      },
      {
        name: "Extended Authorization Attributes",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "extended_authorization_attributes" } },
      },
      {
        name: "Gateway",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "gateway" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Kind",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "kind" } },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToLocationKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "location_id" } },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Message",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "message" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToOrderKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "order_id" } },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Parent",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToParentKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "parent_id" } },
        configuration: {
          relatedModelKey: ShopifyOrderTransactionKey,
        },
      },
      {
        name: "Payment Details",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "payment_details" } },
      },
      {
        name: "Payments Refund Attributes",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "payments_refund_attributes" } },
      },
      {
        name: "Processed At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "processed_at" } },
      },
      {
        name: "Receipt",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "receipt" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "created_at" } },
      },
      {
        name: "Refund",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToRefundKey,
        configuration: {
          relatedModelKey: ShopifyRefundKey,
        },
      },
      {
        name: "Source Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "source_name" } },
      },
      {
        name: "Status",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "status" } },
      },
      {
        name: "Test",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "test" } },
      },
      // {
      //   name: "User",
      //   fieldType: FieldType.BelongsTo,
      //   key: ShopifyOrderTransactionToUserKey,
      //   contributorConfiguration:  { [ShopifyDominion.id]: { shopifyFieldKey: "user_id" } },
      //   configuration: {
      //     relatedModelKey: ShopifyUserKey,
      //   },
      // },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_sublist", resource: "transaction", parent: "order" },
  },
  "2023-01": {
    fields: [
      {
        name: "Payment ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "payment_id" } },
      },
    ],
  },
  "2023-04": {
    fields: [
      {
        name: "Total Unsettled Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_unsettled_set" } },
      },
    ],
  },
  "2024-01": {
    fields: [
      {
        name: "Multicapturable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_multicapturable",
            graphQLFieldKey: "multiCapturable",
            graphQLOnlyField: true,
          },
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "Total Unsettled Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "total_unsettled_set",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Amount",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "amount",
            graphQLFieldKey: "amount",
            skipGraphQLSelection: true,
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Authorization",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "authorization",
            graphQLFieldKey: "authorizationCode",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Authorization Expires At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "authorization_expires_at", graphQLFieldKey: "authorizationExpiresAt" },
        },
      },
      {
        name: "Balance Transactions",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderTransactionToBalanceTransactionsKey,
        configuration: {
          relatedModelKey: ShopifyBalanceTransactionKey,
          inverseFieldKey: ShopifyBalanceTransactionToOrderTransactionKey,
        },
      },
      {
        name: "Children",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderTransactionToChildrenKey,
        configuration: {
          relatedModelKey: ShopifyOrderTransactionKey,
          inverseFieldKey: ShopifyOrderTransactionToParentKey,
        },
      },
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "currency",
            graphQLFieldKey: "currency",
            skipGraphQLSelection: true,
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Error Code",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "error_code",
            graphQLFieldKey: "errorCode",
            isShopifyEnum: true,
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [
            { name: "AMAZON_PAYMENTS_INVALID_PAYMENT_METHOD" },
            { name: "AMAZON_PAYMENTS_MAX_AMOUNT_CHARGED" },
            { name: "AMAZON_PAYMENTS_MAX_AMOUNT_REFUNDED" },
            { name: "AMAZON_PAYMENTS_MAX_AUTHORIZATIONS_CAPTURED" },
            { name: "AMAZON_PAYMENTS_MAX_REFUNDS_PROCESSED" },
            { name: "AMAZON_PAYMENTS_ORDER_REFERENCE_CANCELED" },
            { name: "AMAZON_PAYMENTS_STALE" },
            { name: "CALL_ISSUER" },
            { name: "CARD_DECLINED" },
            { name: "CONFIG_ERROR" },
            { name: "EXPIRED_CARD" },
            { name: "GENERIC_ERROR" },
            { name: "INCORRECT_ADDRESS" },
            { name: "INCORRECT_CVC" },
            { name: "INCORRECT_NUMBER" },
            { name: "INCORRECT_PIN" },
            { name: "INCORRECT_ZIP" },
            { name: "INVALID_AMOUNT" },
            { name: "INVALID_COUNTRY" },
            { name: "INVALID_CVC" },
            { name: "INVALID_EXPIRY_DATE" },
            { name: "INVALID_NUMBER" },
            { name: "PAYMENT_METHOD_UNAVAILABLE" },
            { name: "PICK_UP_CARD" },
            { name: "PROCESSING_ERROR" },
            { name: "TEST_MODE_LIVE_CARD" },
            { name: "UNSUPPORTED_FEATURE" },
          ],
        },
      },
      {
        name: "Extended Authorization Attributes",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "extended_authorization_attributes" } },
        isDeleted: true,
      },
      {
        name: "Gateway",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "gateway",
            graphQLFieldKey: "gateway",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Kind",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "kind",
            graphQLFieldKey: "kind",
            isShopifyEnum: true,
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [
            { name: "AUTHORIZATION" },
            { name: "CAPTURE" },
            { name: "CHANGE" },
            { name: "EMV_AUTHORIZATION" },
            { name: "REFUND" },
            { name: "SALE" },
            { name: "SUGGESTED_REFUND" },
            { name: "VOID" },
          ],
        },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToLocationKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "location_id",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
            graphQLFieldKey: "locationId:order",
            graphQLSelection: `
            retailLocation {
              id
            }
            `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Message",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "message", availableInWebhook: ["order_transactions/create", "refunds/create"] },
        },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "order_id",
            graphQLFieldKey: "order",
            graphQLSelection: `
            id
            sourceName
            `,
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Parent",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToParentKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "parent_id",
            graphQLFieldKey: "parentTransaction",
            graphQLSelection: "id",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyOrderTransactionKey,
        },
      },
      {
        name: "Payment Details",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "payment_details",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
            graphQLFieldKey: "paymentDetails",
            graphQLSelection: `
          ... on CardPaymentDetails {
            avsResultCode
            bin
            company
            cvvResultCode
            expirationMonth
            expirationYear
            name
            number
            paymentMethodName
            wallet
          }
          ... on LocalPaymentMethodsPaymentDetails {
            paymentDescriptor
            paymentMethodName
          }
          ... on ShopPayInstallmentsPaymentDetails {
            paymentMethodName
          }
          `,
          },
        },
      },
      {
        name: "Payments Refund Attributes",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "payments_refund_attributes" } },
        isDeleted: true,
      },
      {
        name: "Processed At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "processed_at",
            graphQLFieldKey: "processedAt",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Receipt",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "receipt",
            graphQLFieldKey: "receiptJson",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "created_at",
            graphQLFieldKey: "createdAt",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Refund",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToRefundKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "refundId", skipGraphQLSelection: true } },
        configuration: {
          relatedModelKey: ShopifyRefundKey,
        },
      },
      {
        name: "Source Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "source_name",
            graphQLFieldKey: "sourceName",
            skipGraphQLSelection: true,
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "status",
            graphQLFieldKey: "status",
            isShopifyEnum: true,
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [
            { name: "AWAITING_RESPONSE" },
            { name: "ERROR" },
            { name: "FAILURE" },
            { name: "PENDING" },
            { name: "SUCCESS" },
            { name: "UNKNOWN" },
          ],
        },
      },
      {
        name: "Test",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "test",
            graphQLFieldKey: "test",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Payment ID",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "payment_id",
            graphQLFieldKey: "paymentId",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Total Unsettled Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "total_unsettled_set",
            graphQLFieldKey: "totalUnsettledSet",
            graphQLSelection: graphqlMoneyBagFieldToRest,
          },
        },
      },
      {
        name: "Multicapturable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_multicapturable",
            graphQLFieldKey: "multiCapturable",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Account Number",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "accountNumber", graphQLOnlyField: true } },
      },
      {
        name: "Amount Rounding Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "amountRoundingSet",
            graphQLSelection: graphqlMoneyBagField,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Amount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "amountSet", graphQLOnlyField: true, graphQLSelection: graphqlMoneyBagField },
        },
      },
      {
        name: "Formatted Gateway",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "formattedGateway", graphQLOnlyField: true } },
      },
      {
        name: "Manually Capturable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "manuallyCapturable", graphQLOnlyField: true } },
      },
      {
        name: "Manual Payment Gateway",
        key: manualPaymentGatewayKey,
        initialStorageEpochKey: manualPaymentGatewayStorageEpochKey,
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "manualPaymentGateway",
            shopifyFieldKey: "manual_payment_gateway",
            availableInWebhook: ["order_transactions/create", "refunds/create"],
          },
        },
      },
      {
        name: "Maximum Refundable V2",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "maximumRefundableV2",
            graphQLOnlyField: true,
            graphQLSelection: `
            amount
            currencyCode
            `,
          },
        },
      },
      {
        name: "Settlement Currency",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "settlementCurrency", graphQLOnlyField: true, isShopifyEnum: true },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: shopifyCurrencyCodeEnumOptions,
        },
      },
      {
        name: "Settlement Currency Rate",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "settlementCurrencyRate", graphQLOnlyField: true } },
      },
      {
        name: "Shopify Payments Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "shopifyPaymentsSet",
            graphQLOnlyField: true,
            graphQLSelection: `
          extendedAuthorizationSet {
            extendedAuthorizationExpiresAt
            standardAuthorizationExpiresAt
          }
          refundSet {
            acquirerReferenceNumber
          }
            `,
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "order.refunds",
      resource: "refund.transactions",
      additionalFields: ["refundId:id"],
    },
  },
  "2025-07": {
    fields: [
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderTransactionToLocationKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "location",
            graphQLOnlyField: true,
            graphQLSelection: `id`,
          },
        },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
    ],
  },
};

export const ShopifyOrderTransaction = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
